// src/pages/RewardBoxesPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import type { AppDispatch, RootState } from "../app/store";
import {
  AssignedRewardBox,
  fetchAssignedRewardBoxes,
  setSelectedRewardBox,
} from "../features/rewardSlice";
import { FaEye as ViewIcon, FaRedoAlt as RetryIcon, FaEdit as EditIcon } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import ManageRewardBoxesPage from "./manage_reward_boxes_page";
import { getAllBoxes } from "../features/boxSlice";
import noImageAvailable from "../assets/no_image.png";
import { getImagePath } from "../utils/utils";

/* ---------- Layout ---------- */
const Container = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 0.5rem;
    text-align: left;
    border-bottom: 1px solid #ddd;
    vertical-align: middle;
  }

  th {
    background-color: #f4f4f4;
    white-space: nowrap;
  }

  /* 7 columns now: # | Reward Box | Box (img+name) | Description | Value | Stock | Actions */
  td:nth-child(1) { width: 6%; }   /* # */
  td:nth-child(2) { width: 16%; }  /* Reward Box */
  td:nth-child(3) { width: 24%; }  /* Box (img+name) */
  td:nth-child(4) { width: 28%; }  /* Description */
  td:nth-child(5) { width: 10%; }  /* Value */
  td:nth-child(6) { width: 8%; }   /* Stock */
  td:nth-child(7) { width: 8%; }   /* Actions */
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IconButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  &:hover { background: rgba(0,0,0,0.05); }
`;

const EmptyState = styled.div`
  padding: 16px;
  color: #666;
`;

const ImageWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

/* ---------- Page ---------- */
const RewardBoxesPage: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();

  const items = useSelector((state: RootState) => state.rewards.assignedRewardBoxes);
  const status = useSelector((state: RootState) => state.rewards.fetchAssignedStatus);
  const error = useSelector((state: RootState) => state.rewards.fetchAssignedError);
  const loading = useSelector((state: RootState) => state.rewards.isFetchAssignedInProgress);

  const allBoxes = useSelector((state: RootState) => state.boxes.allBoxes);
  const [isModalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchAssignedRewardBoxes());
    dispatch(getAllBoxes());
  }, [dispatch]);

  const boxesById = useMemo(() => {
    const map: Record<string, any> = {};
    for (const b of allBoxes || []) {
      if (b?.id != null) map[String(b.id)] = b;
    }
    return map;
  }, [allBoxes]);

  const openUpdateLevelModal = (rewardBox: AssignedRewardBox) => {
    dispatch(setSelectedRewardBox(rewardBox));
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    dispatch(setSelectedRewardBox(null));
  };

  return (
    <>
      <Container>
        <StyledTable>
          <thead>
            <tr>
              <th>#</th>
              <th>Reward Box</th>
              <th>Box</th>
              <th>Description</th>
              <th>Value</th>
              <th>Stock</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading && (
              <tr>
                <td colSpan={7}>Loading assigned reward boxes…</td>
              </tr>
            )}

            {!loading && status === "failed" && (
              <tr>
                <td colSpan={7} style={{ color: "crimson" }}>
                  {String(error ?? "Failed to fetch assigned reward boxes.")}
                  <Actions style={{ display: "inline-flex", marginLeft: 12 }}>
                    <IconButton
                      onClick={() => dispatch(fetchAssignedRewardBoxes())}
                      title="Retry"
                      aria-label="Retry"
                    >
                      <RetryIcon />
                    </IconButton>
                  </Actions>
                </td>
              </tr>
            )}

            {!loading && status === "succeeded" && items.length === 0 && (
              <tr>
                <td colSpan={7}>
                  <EmptyState>No reward boxes assigned yet.</EmptyState>
                </td>
              </tr>
            )}

            {!loading &&
              items.map((rb, idx) => {
                const rewardBoxLabel = rb.box || "—";
                const rewardId = rb.reward_id ?? null;
                const matched = rewardId ? boxesById[String(rewardId)] : null;

                const boxImg =
                  matched?.picture ? getImagePath(matched.picture) : noImageAvailable;
                const boxName = matched?.name ?? (rewardId ? "—" : "");
                const desc = matched?.description ?? (rewardId ? "—" : "");
                const val =
                  matched?.final_value != null ? `$${matched.final_value}` : (rewardId ? "—" : "");
                const stock =
                  matched?.stock_count != null ? matched.stock_count : (rewardId ? "—" : "");

                return (
                  <tr key={`${rewardBoxLabel}-${idx}`}>
                    <td>{idx + 1}</td>
                    <td>{rewardBoxLabel}</td>
                    <td>
                      {rewardId ? (
                        <ImageWrapper>
                          <img
                            src={boxImg}
                            alt={boxName || "Image"}
                            style={{ width: "48px", height: "48px", objectFit: "contain" }}
                          />
                          <div>{boxName}</div>
                        </ImageWrapper>
                      ) : (
                        "" /* no selection yet */
                      )}
                    </td>
                    <td>{desc}</td>
                    <td>{val}</td>
                    <td>{stock}</td>
                    <td>
                      <Actions>
                        {/* Always show Edit */}
                        <IconButton
                          title="Edit"
                          aria-label="Edit"
                          onClick={() => openUpdateLevelModal(rb)}
                        >
                          <EditIcon />
                        </IconButton>

                        {/* Show View only when reward_id exists */}
                        {rewardId && (
                          <IconButton
                            title="View"
                            aria-label="View"
                            onClick={() =>

                                        navigate(`/dashboard/boxes/details/${encodeURIComponent(rewardId)}`)

                            //   navigate(`/dashboard/rewards/${encodeURIComponent(rewardId)}`)
                            }
                          >
                            <ViewIcon />
                          </IconButton>
                        )}
                      </Actions>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </StyledTable>
      </Container>

      {isModalOpen && (
        <ManageRewardBoxesPage isOpen={isModalOpen} onClose={handleCloseModal} />
      )}
    </>
  );
};

export default RewardBoxesPage;
